import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { AccountService, AuthService, DriverInformationService } from '../../../../_shared/services';
import { Account, DriverAssignmentFilter, User } from '../../../../_shared/models';
import { AccountSelectorComponent } from "../../../../_shared/components/account-selector/account-selector.component";
import { DriverInformationSettings } from '../../../../_shared/models/drivers/management/driver-information-settings.model';
import { DriverInformationListService } from '../../services/driver-information-list.service';
import { FormErrorMessageComponent } from "../../../../_shared/components/form-error-message/form-error-message.component";
import { VehicleAssignmentModalComponent } from '../../../_shared/components/modals/driver-assignment/vehicle-assignment-modal.component';
import { UnifiedTreeItem } from '../../../../_shared/models/driver.model';
import { buildAssetTree } from '../../../../_shared/helpers/hierarchy.helper';
import { Subject } from 'rxjs';
import { AssignmentFilterService } from '../../../driver-vehicle-assignment/services/assignment-filter.service';
import { SingleAssignmentModalConfig } from '../../../../_shared/constants/assignment-modal-config.constants';
import { ModalConfig } from '../../../driver-vehicle-assignment/models/vehicle-assignment.model';
import { UploadDriverDocumentsModalComponent } from '../../../driver-documents/components/modals/upload-driver-documents-modal/upload-driver-documents-modal.component';
import { DriverDocumentsService } from '../../../driver-documents/services/driver-documents.service';
import { ConfirmationOptions } from '../../../../_shared/models/modals/confirmation-options.model';
import { ConfirmationModalService } from '../../../../_shared/services/modals/confirmation-modal.service';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { FlatpickrDatePickerComponent } from '../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { UserRole } from '../../../../_shared/models/user-roles.enum';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-add-drivers-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, FormsModule, AccountSelectorComponent, FormErrorMessageComponent, FlatpickrDatePickerComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './add-drivers-dialog.component.html',
  styleUrl: './add-drivers-dialog.component.scss'
})
export class AddDriversDialogComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private driverInformationService: DriverInformationService = inject(DriverInformationService);
  private driverListService: DriverInformationListService = inject(DriverInformationListService);
  private fb: FormBuilder = inject(FormBuilder);
  private modalInstance: NgbActiveModal = inject(NgbActiveModal);
  private toastr: ToastrService = inject(ToastrService);
  private modalService: NgbModal = inject(NgbModal);
  private authService: AuthService = inject(AuthService);
  private assignmentFilterService: AssignmentFilterService = inject(AssignmentFilterService);
  private driverDocumentsService: DriverDocumentsService = inject(DriverDocumentsService);
  private confirmationModalService: ConfirmationModalService = inject(ConfirmationModalService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private accountsService: AccountService = inject(AccountService);
  private transloco: TranslocoService = inject(TranslocoService);
  private cancelSubject: Subject<void> = new Subject<void>();

  selectedAccount: Account;
  driverForm: FormGroup;
  photoFile: File | undefined;
  photoUrl: string | ArrayBuffer | null;
  placeholder = 'assets/images/default_avatar.svg';
  documentFile: File | null = null;
  documentName: string = '';
  documentUrl: string | ArrayBuffer | null = null;
  isAssignVehicleModalOpen: boolean = false;
  selectedVehicle: any[] = [];
  submitted: boolean = false;
  isLoading: boolean = false;
  alertMessage: string;
  oldIdNumber: string;
  placeholderText: string = "";
  selectedDocumentType: string = 'ID';
  hierarchicalAssetsTree: UnifiedTreeItem[] = [];
  assignmentFilter = { drivers: [], assets: [] }
  user: User;
  hasFleetManagerRole: boolean = false;
  assetsLoaded: WritableSignal<boolean> = signal(false);
  dob: string = '';
  ngOnInit() {
    this.user = this.authService.getUser();
    this.hasFleetManagerRole = this.authService.hasRole(UserRole.GTFleetManager);
    this.updatePlaceholder('ID');
    this.validateForm();
    this.driverForm.disable();
  }

  ngOnDestroy(): void {
    if (this.cancelSubject) {
      this.cancelSubject.next();
      this.cancelSubject.complete();
    }
  }

  validateForm() {
    this.driverForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{10,15}$/), this.digitsOnlyValidator]],
      altPhoneNumber: ['', [Validators.pattern(/^\d{10,15}$/), this.digitsOnlyValidator]],
      email: ['', [Validators.required, Validators.email]],
      address: ['', Validators.required],
      idNumber: ['', Validators.required]
    });
  }

  onSelectAccount(account: Account) {
    this.selectedAccount = account;

    if (this.selectedAccount) {
      this.driverForm.enable();
    }
  }

  clearSelectedPhoto() {
    this.photoFile = undefined;
    this.photoUrl = this.placeholder;
  }

  digitsOnlyValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const digitsOnly = /^\d+$/;
    const result = value && !digitsOnly.test(value) ? { digitsOnly: true } : null;
    return result;
  }

  updatePlaceholder(documentType: string) {
    this.selectedDocumentType = documentType;
    this.placeholderText = this.transloco.translate(
      documentType === 'ID' ? 'driverInformation.addDialog.enterIdNumber' : 'driverInformation.addDialog.enterPassportNumber'
    );
    this.alertMessage = '';
  }

  getErrorMessage(field: string): string | null {
    const control = this.driverForm.get(field);

    if (control?.hasError('required')) {
      return this.transloco.translate('driverInformation.validation.required');
    }

    if (control?.hasError('digitsOnly')) {
      return this.transloco.translate('driverInformation.validation.digitsOnly');
    }

    if (control?.hasError('pattern') && (field === 'phoneNumber' || field === 'altPhoneNumber')) {
      return this.transloco.translate('driverInformation.validation.phoneLength');
    }

    if (control?.hasError('email')) {
      return this.transloco.translate('driverInformation.validation.invalidEmail');
    }

    return null;
  }

  onAddDriverPhoto(event: any) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => this.photoUrl = reader.result;
      reader.readAsDataURL(file);

      this.photoFile = file;
    }
  }

  async onSubmitDriverForm() {
    const driver = await this.addDriver();

    if (!this.hasFleetManagerRole && driver && driver.id > 0) {
      this.toastr.success(this.transloco.translate('driverInformation.toasts.driverAdded'));
      this.modalInstance.close();
      this.driverListService.refreshDriverList();

      this.openUploadDriverDocumentModal(driver.id);
      return;
    }

    if (this.hasFleetManagerRole && driver && driver.id > 0) {
      await this.getUserAssetsAsync();
      this.assignmentFilterService.setReadOnlyBillingAccount(driver.billingAccountId);
      this.toastr.success(this.transloco.translate('driverInformation.toasts.driverAdded'));
      this.driverListService.refreshDriverList();
      this.modalInstance.close();

      const uploadRef = await this.openUploadDriverDocumentModal(driver.id);

      uploadRef.closed.subscribe(() => {
        this.openAssignmentConfirmationModal(driver.id);
      });

      return;
    }
  }

  async openUploadDriverDocumentModal(driverId: number): Promise<NgbModalRef> {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'custom-modal-window'
    };

    const modalRef = this.modalService.open(UploadDriverDocumentsModalComponent, modalOptions);
    const documentTypes = (await this.driverDocumentsService.getDocumentTypes()).data || [];
    modalRef.componentInstance.documentTypes = documentTypes;
    modalRef.componentInstance.driverId = driverId;

    const driversLicense = documentTypes.find(type => type.name === 'Drivers License');
    if (driversLicense) {
      modalRef.componentInstance.selectedDocumentType = driversLicense.name;
      modalRef.componentInstance.driversLicenseRequired = true;
    }

    modalRef.closed.subscribe(() => {
      this.toastr.success(this.transloco.translate('driverInformation.toasts.documentsUploaded'));
    });

    return modalRef;
  }

  openAssignmentConfirmationModal(driverId: number) {
    const options: ConfirmationOptions = {
      title: this.transloco.translate('driverInformation.assignVehiclesPrompt.title'),
      message: this.transloco.translate('driverInformation.assignVehiclesPrompt.message'),
      confirmText: this.transloco.translate('common.yes'),
      declineText: this.transloco.translate('common.no')
    }

    this.confirmationModalService.showConfirmationModal(options).then(async () => {
      this.openSingleAssignmentModal(driverId);
    });
  }

  openSingleAssignmentModal(driverId: number): void {
    const config = SingleAssignmentModalConfig;
    const modalConfig: ModalConfig = {
      ...config,
      title: this.transloco.translate('vehicleAssignment.modals.assignDriver.title'),
      confirmationButtonText: this.transloco.translate('common.assign'),
      cancelButtonText: this.transloco.translate('common.cancel'),
      hierarchicalAssetsTree: this.hierarchicalAssetsTree,
      assignmentFilter: {
        drivers: [{
          id: driverId
        }]
      } as DriverAssignmentFilter,
      assetsLoaded: this.assetsLoaded,
    };

    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static', size: 'xl' };
    const modalRef = this.modalService.open(VehicleAssignmentModalComponent, modalOptions);
    modalRef.componentInstance.config = modalConfig;
  }

  async getUserAssetsAsync() {
    if (!!this.user) {
      this.isLoading = true;
      try {
        this.cancelSubject.next();
        const response = await this.accountsService.getUserAccountAssets([], this.cancelSubject);
        if (response.data) {
          this.accountsService.setAssets(response.data);
          this.hierarchicalAssetsTree = buildAssetTree(response.data);
        }
      } catch (error: any) {
        this.errorHandlerService.handleError(error);
      } finally {
        this.isLoading = false;
        this.assetsLoaded.set(true);
      }
    }
  }

  async addDriver() {
    if (this.driverForm.valid && this.dob) {
      this.submitted = true;
      this.isLoading = true;
      this.alertMessage = '';
      try {
        const formData = this.getFormData();
        const response = await this.driverInformationService.createNewDriver(formData);

        return response.data;
      } catch (error: any) {
        this.errorHandlerService.handleError(error, {
          showToast: false,
          callback: (errorMessage: string, errorCode: string) => {
            if (errorCode == "driver-information/driver-id-exists" || errorCode == "driver-information/driver-passport-exists") {
              this.oldIdNumber = this.driverForm.get('idNumber')?.value;
              this.alertMessage = errorMessage;
            }
          }
        });

        return;
      } finally {
        this.submitted = false;
        this.isLoading = false;
      }
    } else {
      Object.values(this.driverForm.controls).forEach(control => {
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });

      return;
    }
  }

  async createDriverSettings(driverId: number): Promise<void> {
    try {
      const driverSettings: DriverInformationSettings = {
        driverId: driverId,
        compensation: 1,
        startTime: '08:00:00',
        shiftDuration: 32400,
        standardRate: 1,
        saturdayRate: 1,
        holidayRate: 1,
        hourlyRate: 79.99,
        daysIn: 4,
        daysOff: 2,
        shiftStartDate: new Date('2025-01-01T00:00:00.000Z'),
        isShiftTimeEnabled: true
      }

      await this.driverInformationService.upsertDriverSettings(driverSettings);
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: false,
        callback: () => {
          this.toastr.warning(this.transloco.translate('driverInformation.toasts.settingsCreateError'));
        }
      });
    }
  }

  closeModal() {
    this.clearSelectedPhoto();
    this.modalInstance.dismiss();
  }

  private getFormData(): FormData {
    const formData = new FormData();

    formData.append('AccountId', this.selectedAccount?.id?.toString() || '');
    formData.append('FirstName', this.driverForm.get('firstName')?.value || '');
    formData.append('LastName', this.driverForm.get('lastName')?.value || '');
    formData.append('Email', this.driverForm.get('email')?.value || '');
    formData.append('DateOfBirth', this.dob || '');
    formData.append('PhoneNumber', this.driverForm.get('phoneNumber')?.value || '');
    formData.append('AlternativePhoneNumber', this.driverForm.get('altPhoneNumber')?.value || '');
    formData.append('Address', this.driverForm.get('address')?.value || '');
    formData.append('HasProfilePicture', !!this.photoFile ? 'true' : 'false');

    if (this.selectedDocumentType === 'ID') {
      formData.append('DriverIdNumber', this.driverForm.get('idNumber')?.value || '');
    } else {
      formData.append('PassportNumber', this.driverForm.get('idNumber')?.value || '');
    }

    if (this.photoFile !== undefined) {
      formData.append('ProfilePicture', this.photoFile);
    }

    return formData;
  }
}